package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.render.model.ImageStyle
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType

/**
 * 渲染桥接的 commonMain 消费面镜像（ADR-0072 决策 2）。
 *
 * 真源为 `platform-client-api` 的 `top.wcpe.allincore.platform.client.api.render.UiRenderBridge`
 * （纯 Java 8 对外 API，按 ADR-0002 不得 KMP 化）。本接口按「ui-runtime 实际调用的方法面」
 * 显式化既有事实接口：= LayerBufferingBridge 的全部录制面（DrawRecord 字段面 / replayDrawCall 分支）
 * + 状态栈 push/pop 组 + 渲染器层实际调用的查询方法（textWidth / screenWidth / textureExists 等）。
 * **各方法语义与 platform UiRenderBridge 同名方法逐一一致**（含 default 降级链），KDoc 不再重复真源全文。
 *
 * 参数中的值对象 / 枚举一律使用 ADR-0072 决策 1 的 `render.model` 中立镜像类型；
 * JVM 侧经 [RenderBridgeAdapter]（jvmMain）把本接口适配回 platform UiRenderBridge，
 * Web 端（FR-UI-024）由 PixiJS 后端直接实现本接口。
 *
 * drawItemStack 重载收敛评估结论：LayerBufferingBridge 录制面依赖 6/7/8/11/12/13 参共 6 个重载
 * 各自的默认字段语义（如 6 参路径不写 showCount），为保 DrawRecord 录制逐位一致，保留同一重载集，
 * 不再折叠为默认参数（折叠会改变部分调用点的录制字段）。
 *
 * <p>所有坐标用浮点像素，原点 (0,0) 在屏幕左上角。颜色使用 ARGB int（0xAARRGGBB）。</p>
 */
@Suppress("TooManyFunctions", "ComplexInterface", "LongParameterList", "MagicNumber")
interface UiRenderBridge {

    // ===== 基础绘制 =====

    /** 填充矩形。argb=0 时实现可跳过。 */
    fun fillRect(x: Float, y: Float, width: Float, height: Float, argb: Int)

    /** 绘制文本。scale 表示文本缩放（1.0 为 MC 默认字号）。 */
    fun drawText(text: String, x: Float, y: Float, argb: Int, scale: Float)

    /** 绘制文本（带字体样式）。 */
    fun drawText(
        text: String,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
    ) {
        drawText(text, x, y, argb, scale)
    }

    /** 当前平台是否支持内联富文本（`[kind:id]` 图标标签 + 图文混排 + vanilla `§` 颜色码）。 */
    fun supportsRichText(): Boolean = false

    /** 绘制一段内联富文本；默认剥标签按纯文本降级（零回归）。 */
    fun drawRichText(
        text: String,
        x: Float,
        y: Float,
        argb: Int,
        scale: Float,
        fontName: String?,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
        maxWidth: Float,
        multiline: Boolean,
        lineStep: Float,
    ) {
        drawText(stripInlineTags(text), x, y, argb, scale, fontName, shadow, bold, italic)
    }

    /** 度量内联富文本占用尺寸 `[宽, 高]`；默认剥标签按纯文本估算（行高 9×scale）。 */
    fun measureRichText(
        text: String,
        scale: Float,
        fontName: String?,
        bold: Boolean,
        maxWidth: Float,
        multiline: Boolean,
    ): FloatArray {
        val plain = stripInlineTags(text)
        return floatArrayOf(textWidth(plain, scale, fontName, bold), RICH_TEXT_LINE_HEIGHT * scale)
    }

    /** 绘制一条水平/垂直线段（实现可用细矩形）。 */
    fun drawLine(x1: Float, y1: Float, x2: Float, y2: Float, thickness: Float, argb: Int)

    // ===== 裁剪 / 变换 / 透明度 / 色彩矩阵状态栈 =====

    /** 推入裁剪区域；需配对调用 [popClip]。 */
    fun pushClip(x: Float, y: Float, width: Float, height: Float)

    /** 弹出最近一次裁剪。 */
    fun popClip()

    /** 当前平台是否支持运行时变换矩阵。 */
    fun supportsRuntimeTransform(): Boolean = false

    /** 推入运行时变换；需与 [popTransform] 成对调用。 */
    fun pushTransform(tx: Float, ty: Float, scaleX: Float, scaleY: Float) {}

    /** 弹出最近一次运行时变换。 */
    fun popTransform() {}

    /** 当前平台是否支持运行时透明度乘子。 */
    fun supportsRuntimeAlpha(): Boolean = false

    /** 推入透明度乘子；需与 [popAlpha] 成对调用。 */
    fun pushAlpha(alpha: Float) {}

    /** 弹出最近一次透明度乘子。 */
    fun popAlpha() {}

    /** 当前平台是否支持运行时色彩矩阵（CSS filter 系）。 */
    fun supportsColorMatrix(): Boolean = false

    /** 推入 4×5 行优先颜色矩阵（20 元素）；需与 [popColorMatrix] 成对调用。 */
    fun pushColorMatrix(matrix4x5: FloatArray) {}

    /** 弹出最近一次 [pushColorMatrix] 推入的颜色矩阵。 */
    fun popColorMatrix() {}

    /** 当前平台是否支持完整 3×3 仿射变换（旋转/倾斜/透视）。 */
    fun supportsFullTransform(): Boolean = false

    /** 推入 3×3 行优先仿射矩阵变换；需与 [popTransform3x3] 成对调用。 */
    fun pushTransform3x3(mat3x3: FloatArray) {}

    /** 弹出最近一次 [pushTransform3x3] 推入的变换。 */
    fun popTransform3x3() {}

    /** 推入 4×4 列优先矩阵变换（3D 透视/绕 X/Y 轴旋转）；需与 [popTransform4x4] 成对调用。 */
    fun pushTransform4x4(mat4x4: FloatArray) {}

    /** 弹出最近一次 [pushTransform4x4] 推入的变换。 */
    fun popTransform4x4() {}

    // ===== filter 组 / backdrop / 混合模式 =====

    /** 当前平台是否支持 filter 组离屏捕获（CSS filter: blur）。 */
    fun supportsFilterGroup(): Boolean = false

    /** 开启一个 filter 组：其后到 [endFilterGroup] 之间的绘制重定向到离屏并做高斯模糊合成。 */
    fun beginFilterGroup(blurRadius: Float, x: Float, y: Float, w: Float, h: Float) {}

    /** 结束最近一次 [beginFilterGroup]。 */
    fun endFilterGroup() {}

    /** 当前平台是否支持 backdrop-filter 背景模糊（毛玻璃）。 */
    fun supportsBackdropFilter(): Boolean = false

    /** 应用 backdrop-filter 背景模糊：就地采样元素矩形背后已落盘像素做高斯画回同 rect。 */
    fun applyBackdropBlur(blurRadius: Float, x: Float, y: Float, w: Float, h: Float) {}

    /** 当前平台是否支持 mix-blend-mode 混合模式。 */
    fun supportsBlendMode(): Boolean = false

    /**
     * 推入一种 GUI 混合模式；需与 [popBlendMode] 成对调用。
     *
     * @param mode `GuiBlendMode` 的序数（`ordinal`，镜像保序由一致性测试冻结）
     */
    fun pushBlendMode(mode: Int) {}

    /** 弹出最近一次 [pushBlendMode]。 */
    fun popBlendMode() {}

    // ===== historyLayer（FR-UI-023）/ 材质层 / 粒子层（FR-UI-031） =====

    /**
     * 当前平台是否支持 historyLayer 局部低清跨帧历史采样（FR-UI-023）。
     * 返回 false 时上层完全跳过 [beginHistoryCapture]/[endHistoryCapture]（元素照常渲染、无历史余晖）。
     */
    fun supportsHistoryCapture(): Boolean = false

    /**
     * 开启一个历史捕获组（FR-UI-023 historyLayer）：把其后到 [endHistoryCapture] 之间对本元素的绘制
     * 重定向到离屏隔离靶（source=self 时），以便在 end 时捕获元素自身像素入环形缓冲并叠加历史余晖。
     */
    fun beginHistoryCapture(elementId: String, spec: Map<String, *>, x: Float, y: Float, w: Float, h: Float) {}

    /** 结束最近一次 [beginHistoryCapture]：捕获元素自身像素入环形缓冲、叠加历史余晖合成回主屏。 */
    fun endHistoryCapture() {}

    /**
     * 绘制程序化材质层（FR-UI-020）。[kind] 为材质层类型，[fields] 为 IR 字段快照；
     * 默认空实现保证旧平台与测试平台零回归。
     */
    fun drawMaterialLayer(kind: String, fields: Map<String, *>, x: Float, y: Float, w: Float, h: Float) {}

    /**
     * 当前平台是否支持 FR-UI-031 粒子层（GPU 实例化 quad + 有状态演化）。
     * 返回 false 时上层完全跳过 [drawParticleLayer]（元素照常渲染、无粒子）。
     */
    fun supportsParticles(): Boolean = false

    /**
     * 绘制一个粒子层（FR-UI-031 / ADR-0071）。单调用、非作用域 begin/end：
     * 粒子持续演化状态由平台按 (elementId, ordinal) 持久维持，本调用只提交本帧绘制请求。
     */
    fun drawParticleLayer(
        elementId: String,
        ordinal: Int,
        spec: Map<String, *>,
        x: Float,
        y: Float,
        w: Float,
        h: Float,
    ) {}

    // ===== z 层级 / flush =====

    /** 推入 z 层级偏移；需与 [popZ] 成对调用。 */
    fun pushZ(z: Float) {}

    /** 弹出最近一次 z 层级偏移。 */
    fun popZ() {}

    /** 立即提交当前累积的延迟顶点，清空平台内部的 batch 缓冲。 */
    fun flush() {}

    // ===== 查询 =====

    /** 估算 text 在指定 scale 下的像素宽度，供布局/对齐使用。 */
    fun textWidth(text: String, scale: Float): Float

    /** 估算 text 在指定字体和 scale 下的像素宽度。 */
    fun textWidth(text: String, scale: Float, fontName: String?, bold: Boolean): Float = textWidth(text, scale)

    /** 可用绘制区域宽度（像素）；默认正无穷表示不限制。 */
    fun screenWidth(): Float = Float.POSITIVE_INFINITY

    /** 可用绘制区域高度（像素）；语义同 [screenWidth]。 */
    fun screenHeight(): Float = Float.POSITIVE_INFINITY

    /** 检查贴图资源是否存在；默认保守返回 false（上层回退纯色背景）。 */
    fun textureExists(resource: String): Boolean = false

    /** 返回不带任何坐标偏移的「屏幕空间」渲染桥；默认返回自身。 */
    fun screenSpaceBridge(): UiRenderBridge = this

    /** 当前平台是否支持批量提交渲染命令。 */
    fun supportsBatching(): Boolean = false

    /** 单帧内允许走离屏模糊路径的阴影元素最大数量；超阈值降级为简易投影矩形。 */
    fun maxShadowedElementsPerFrame(): Int = DEFAULT_MAX_SHADOWED_ELEMENTS

    // ===== 贴图 / 物品 / 实体 =====

    /** 绘制贴图；找不到资源或未实现时默认画粉色占位矩形，便于问题定位。 */
    fun drawImage(resource: String, x: Float, y: Float, width: Float, height: Float) {
        // 默认实现：粉色占位，避免静默丢图
        fillRect(x, y, width, height, PLACEHOLDER_PINK)
    }

    /** 绘制带纹理变换样式的贴图；默认忽略 style 直接调用 [drawImage]（兜底降级）。 */
    fun drawImageStyled(resource: String, x: Float, y: Float, w: Float, h: Float, style: ImageStyle?) {
        drawImage(resource, x, y, w, h)
    }

    /** 绘制贴图子区域（uv 切片）；默认忽略 uv 调 [drawImage]（视觉退化但不丢图）。 */
    fun drawTexturedQuad(
        resource: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        uvX: Float,
        uvY: Float,
        uvW: Float,
        uvH: Float,
    ) {
        drawImage(resource, x, y, width, height)
    }

    /** 绘制 Minecraft 物品图标（含数量徽标）；默认粉色占位。 */
    fun drawItemStack(itemId: String, count: Int, x: Float, y: Float, width: Float, height: Float) {
        // 默认实现：粉色占位
        fillRect(x, y, width, height, PLACEHOLDER_PINK)
    }

    /** 绘制 Minecraft 物品图标（带附魔光效开关）；默认转发到不带 enchanted 的旧版本。 */
    fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemStack(itemId, count, x, y, width, height)
    }

    /** 绘制 Minecraft 物品图标（含数量角标开关）；默认转发到带锚点的规范重载（vanilla 右下角 + 零偏移）。 */
    fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemStack(itemId, count, enchanted, showCount, "bottom-right", 0f, 0f, x, y, width, height)
    }

    /**
     * 绘制 Minecraft 物品图标（规范重载：携带数量徽标的 9 宫格锚点与偏移）。
     * 默认丢弃锚点/偏移与 showCount 转发到 enchanted 重载（不可回转 8 参重载，否则互相递归——与真源一致）。
     */
    fun drawItemStack(
        itemId: String,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemStack(itemId, count, enchanted, x, y, width, height)
    }

    /** 绘制 Minecraft 物品图标（携带物品 SNBT 的规范重载）；默认丢弃 itemNbt 转发到 11 参重载。 */
    fun drawItemStack(
        itemId: String,
        itemNbt: String?,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemStack(itemId, count, enchanted, showCount, countAnchor, countOffsetX, countOffsetY, x, y, width, height)
    }

    /** 绘制 Minecraft 物品图标（携带「背景色变暗遮罩」的规范重载，issue #68）；默认丢弃遮罩色转发 12 参。 */
    fun drawItemStack(
        itemId: String,
        itemNbt: String?,
        count: Int,
        enchanted: Boolean,
        showCount: Boolean,
        countAnchor: String?,
        countOffsetX: Float,
        countOffsetY: Float,
        backdropColor: Int,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
    ) {
        drawItemStack(
            itemId, itemNbt, count, enchanted, showCount, countAnchor, countOffsetX, countOffsetY,
            x, y, width, height,
        )
    }

    /** 绘制 2D 实体预览（朝向跟随鼠标）；默认空引用直接返回，否则画淡蓝色占位矩形。 */
    fun drawEntityPreview(
        entityRef: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
    ) {
        if (entityRef.isEmpty()) return
        fillRect(x, y, width, height, PLACEHOLDER_BLUE)
    }

    /** 绘制 2D 实体预览（12 参扩展：实体类型 / 缩放 / 旋转）；默认转发到 7 参旧版本。 */
    fun drawEntityPreview(
        entityRef: String,
        entityType: String?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        followMouse: Boolean,
    ) {
        drawEntityPreview(entityRef, x, y, width, height, mouseX, mouseY)
    }

    /** 绘制 2D 实体预览（13 参扩展：额外携带 entityName 写入虚拟实体 customName）；默认转发 12 参。 */
    fun drawEntityPreview(
        entityRef: String,
        entityType: String?,
        entityName: String?,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        mouseX: Float,
        mouseY: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        followMouse: Boolean,
    ) {
        drawEntityPreview(
            entityRef, entityType, x, y, width, height, mouseX, mouseY,
            scale, rotationX, rotationY, followMouse,
        )
    }

    /**
     * 绘制独立模型预览。默认桥在原矩形绘制背景与中文诊断，
     * 不把二维纹理冒充成功模型，也不复用实体预览语义。
     */
    fun drawModelPreview(
        source: String,
        modelBase: String,
        texture: String?,
        instanceKey: String,
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        scale: Float,
        rotationX: Float,
        rotationY: Float,
        interactive: Boolean,
        backgroundColor: Int,
    ) {
        if ((backgroundColor ushr 24) != 0) fillRect(x, y, width, height, backgroundColor)
        drawText(MODEL_PREVIEW_UNAVAILABLE, x + 4f, y + 4f, MODEL_PREVIEW_DIAGNOSTIC_COLOR, 0.75f)
    }

    // ===== 特效 =====

    /** 应用霓虹外发光特效；默认空实现（未实现 GPU shader 的平台/测试环境直接跳过）。 */
    fun applyNeonGlow(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        neonColor: Int,
        intensity: Float,
        size: Float,
        animated: Boolean,
        cornerRadii: FloatArray?,
    ) {}

    /** 应用涟漪扩散特效；默认空实现。 */
    fun applyRippleEffect(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        color: Int,
        progress: Float,
        radius: Float,
        centerX: Float,
        centerY: Float,
    ) {}

    /** 应用涟漪扩散特效（带圆角裁剪）；默认转发到不带圆角的重载。 */
    fun applyRippleEffect(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        color: Int,
        progress: Float,
        radius: Float,
        centerX: Float,
        centerY: Float,
        cornerRadii: FloatArray?,
    ) {
        applyRippleEffect(x, y, width, height, color, progress, radius, centerX, centerY)
    }

    /** 应用程序纹理特效（checker/brick/circuit/...）；默认空实现。 */
    fun applyProceduralTexture(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        kind: String?,
        colorA: Int,
        colorB: Int,
        scale: Float,
        density: Float,
        intensity: Float,
        cornerRadii: FloatArray?,
    ) {}

    /** 应用流光特效（linear/radial/diagonal）；默认空实现。 */
    fun applyFlow(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        flowColor: Int,
        speedHz: Float,
        widthPx: Float,
        angleDeg: Float,
        mode: Int,
        cornerRadii: FloatArray?,
    ) {}

    /** 应用能量场特效（脉冲/波浪/电弧/等离子）；默认空实现。 */
    fun applyEnergy(
        x: Float,
        y: Float,
        width: Float,
        height: Float,
        energyColor: Int,
        intensity: Float,
        frequency: Float,
        speed: Float,
        pattern: Int,
        cornerRadii: FloatArray?,
    ) {}

    // ===== 批量提交 =====

    /**
     * 批量提交渲染命令；默认逐条降级：ROUNDED_RECT/GRADIENT 降 fillRect 近似（渐变用色带条纹近似）、
     * SHADOW 降偏移模糊环近似（与真源 FallbackHelper 降级逐位一致）。
     */
    fun submitBatch(batch: RenderBatch) {
        for (cmd in batch.commands) {
            when (cmd.type) {
                RenderCommandType.FILL_RECT -> fillRect(cmd.x, cmd.y, cmd.w, cmd.h, cmd.argb)
                RenderCommandType.DRAW_TEXT -> drawText(cmd.text.orEmpty(), cmd.x, cmd.y, cmd.argb, cmd.scale)
                RenderCommandType.DRAW_LINE -> drawLine(cmd.x, cmd.y, cmd.w, cmd.h, 1f, cmd.argb)
                RenderCommandType.DRAW_IMAGE -> drawImage(cmd.texture.orEmpty(), cmd.x, cmd.y, cmd.w, cmd.h)
                RenderCommandType.ROUNDED_RECT -> fillRect(cmd.x, cmd.y, cmd.w, cmd.h, cmd.argb)
                RenderCommandType.GRADIENT -> gradientFallback(this, cmd)
                RenderCommandType.SHADOW -> shadowFallback(this, cmd)
                RenderCommandType.IMAGE_STYLED ->
                    drawImageStyled(cmd.texture.orEmpty(), cmd.x, cmd.y, cmd.w, cmd.h, cmd.imageStyle)
                RenderCommandType.DRAW_ITEM_STACK ->
                    // 走携带 nbt 的 12 参规范重载并补等价默认值，与真源默认降级一致
                    drawItemStack(
                        cmd.itemId.orEmpty(), cmd.itemNbt, cmd.itemCount, false, true,
                        "bottom-right", 0f, 0f, cmd.x, cmd.y, cmd.w, cmd.h,
                    )
                RenderCommandType.DRAW_TEXTURED_QUAD ->
                    drawTexturedQuad(
                        cmd.texture.orEmpty(), cmd.x, cmd.y, cmd.w, cmd.h,
                        cmd.uvX, cmd.uvY, cmd.uvW, cmd.uvH,
                    )
                RenderCommandType.DRAW_ENTITY_PREVIEW ->
                    drawEntityPreview(cmd.entityRef.orEmpty(), cmd.x, cmd.y, cmd.w, cmd.h, cmd.mouseX, cmd.mouseY)
                else -> Unit
            }
        }
    }

    companion object {
        /** 默认桥无法渲染三维模型时显示的规范诊断。 */
        private const val MODEL_PREVIEW_UNAVAILABLE = "模型预览不可用：当前渲染桥不支持 3D"

        /** 诊断文字使用不透明浅红色，确保透明背景下仍可观察。 */
        private const val MODEL_PREVIEW_DIAGNOSTIC_COLOR: Int = 0xFFFF6B6B.toInt()

        /** 粉色占位色（资源缺失兜底，与真源一致）。 */
        private const val PLACEHOLDER_PINK: Int = 0xFFFF00FF.toInt()

        /** 淡蓝色实体预览占位色（与真源一致）。 */
        private const val PLACEHOLDER_BLUE: Int = 0xFF87CEEB.toInt()

        /** vanilla 行高（像素，scale=1 时），供富文本默认度量。 */
        private const val RICH_TEXT_LINE_HEIGHT = 9f

        /** 单帧离屏模糊阴影元素默认上限（与真源一致）。 */
        private const val DEFAULT_MAX_SHADOWED_ELEMENTS = 20

        /**
         * 剥除内联富文本标签段：与真源 `UiRenderBridge.stripInlineTags` 一致，
         * 把不含嵌套方括号的成对方括号段整段去掉，保留普通文本与 vanilla `§` 颜色码。
         */
        fun stripInlineTags(text: String?): String {
            if (text == null) {
                return ""
            }
            return text.replace(STRIP_TAG_REGEX, "")
        }

        /** 与真源正则 `\[[^\[\]]*]` 等价：仅匹配不含嵌套方括号的成对方括号段。 */
        private val STRIP_TAG_REGEX = Regex("""\[[^\[\]]*]""")
    }
}
